import {useMutation, useQuery, useSuspenseQuery} from "@apollo/client";
import {useEffect} from "react";
import {useNavigate, useSearchParams} from "react-router";

import Button from "@/components/button/Button.tsx";
import {RequiredFieldsNote} from "@/components/form/requiredFieldsNote/RequiredFieldsNote.tsx";
import {CurrentUserContext, GQL_USER_CONTEXT} from "@/lib/queries/accounts.ts";
import {
  GQL_INSTANT_SUBSCRIBE_MUTATION,
  GQL_INSTANT_SUBSCRIPTION_QUERY,
  InstantSubscribeResponse,
  InstantSubscriptionQueryResponse,
} from "@/lib/queries/auth.ts";
import {BillingPeriod} from "@/lib/types/billing.ts";
import {AccountInfoForm, AccountInfoFormSchema} from "@/pages/auth/register/components/AccountInfoForm.tsx";
import {StickyFooter} from "@/pages/auth/register/components/StickyFooter.tsx";
import {PaymentMethodForm} from "@/pages/billing/payment-method/PaymentMethodForm.tsx";
import {CostBreakdown} from "@/pages/billing/v2/summary/CostBreakdown.tsx";

export const RegisterPageContent = () => {
  const {data: userData} = useQuery<CurrentUserContext>(GQL_USER_CONTEXT, {context: {skipAuthError: true}});
  const [queryParams] = useSearchParams();
  const billingPeriod: BillingPeriod =
    queryParams.get("billingPeriod") === "yearly" ? BillingPeriod.YEAR : BillingPeriod.MONTH;
  const selectedTiersParam: string = queryParams.get("selectedTier") || "website_monitoring_tier_01";
  const selectedTiers: string[] = selectedTiersParam
    .split(",")
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
  const {data} = useSuspenseQuery<InstantSubscriptionQueryResponse>(GQL_INSTANT_SUBSCRIPTION_QUERY, {
    variables: {
      billingPeriod,
      selectedTiers,
    },
  });
  const {tiers, braintreeToken} = data.instantSubscriptionData;
  const bundle = tiers || [];
  const [instantSubscribeMutation, {data: instantSubscribeResponse, loading}] =
    useMutation<InstantSubscribeResponse>(GQL_INSTANT_SUBSCRIBE_MUTATION);
  const navigateFn = useNavigate();
  if (userData !== undefined) {
    // if user is already authorized - send them to subscription page with preselected tier
    navigateFn("/billing?action=subscribe", {state: {selectedTier: tiers.at(0)}});
  }
  useEffect(() => {
    // Hide the sticky bottom drawer on mobile as soon as CostBreakdown element becomes
    // visible from the bottom side of the screen
    const targetElement = document.getElementById("costBreakdownContainer");
    const stickyFooter = document.getElementById("bottomStickyFooter");
    const callback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          stickyFooter?.classList.add("d-none");
        } else {
          stickyFooter?.classList.remove("d-none");
        }
      });
    };
    const observer = new IntersectionObserver(callback, {
      root: null, // Use the viewport as the root
      threshold: 0.35,
    });
    if (targetElement) {
      observer.observe(targetElement);
    }
  }, []);
  return (
    <>
      <div className="row pb-4 mb-4">
        <div className="col-12 col-md-7">
          <PaymentMethodForm
            beforeFormContent={
              <>
                <div className="text-center text-md-start mb-4 pulseTextH2Bold pulse-color-gray-800">
                  Account Information
                </div>
                <hr className="mb-4 border-15" />
                <RequiredFieldsNote />
                <AccountInfoForm />
                <div className="text-center text-md-start my-4 pulseTextH2Bold pulse-color-gray-800">
                  Enter Payment Details
                </div>
                <hr className="mb-4 border-15" />
              </>
            }
            hasRequiredFields={false}
            extraScheme={AccountInfoFormSchema}
            formId={"registerPaymentMethodForm"}
            serverErrors={instantSubscribeResponse?.instantSubscribe.errors || {}}
            braintreeToken={braintreeToken}
            billingData={{}}
            variant="narrow"
            onSave={async (formValues, nonce, paymentMethodType) => {
              const result = await instantSubscribeMutation({
                variables: {
                  data: {
                    billing_period: billingPeriod,
                    selected_tier_ids: selectedTiers,
                    payment_method_nonce: nonce,
                    payment_method: paymentMethodType,
                    ...formValues,
                  },
                },
              });
              if (result?.data?.instantSubscribe.success === true) {
                navigateFn("/onboarding");
              }
            }}
          />
        </div>
        <div className="col-12 col-md-5 pb-3">
          <CostBreakdown
            title="Order Summary"
            variant="success"
            billingPeriod={billingPeriod}
            items={bundle}
            disabled={loading}
          >
            <Button
              id="mainSubmitButton"
              type="submit"
              className="w-100 text-uppercase"
              size="large"
              form="registerPaymentMethodForm"
              variant="marketingPrimary"
            >
              {loading ? "Working..." : "Complete Checkout"}
            </Button>
          </CostBreakdown>
        </div>
      </div>
      <StickyFooter loading={loading} formId={"registerPaymentMethodForm"} />
    </>
  );
};
